import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBackButton } from '../components/AppBackButton';
import { ArrowLeft, BookOpen, Check, Download, Eye, Loader2, Sparkles, X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dbManager } from '../services/db_manager';
import { localFlashcardDb } from '../services/flashcardLocalDb';
import { STORE_TRANSLATIONS, STORE_LANGUAGES } from './FlashcardStorePage';

const LOCAL_STORE_TRANSLATIONS: Record<string, Record<string, string>> = {
  pt: {
    backToStore: "Voltar para a Loja",
    decksTitle: "Baralhos Disponíveis",
    size: "Tamanho",
    downloaded: "Salvo localmente",
    notDownloaded: "Não baixado",
    imported: "Importado",
    notImported: "Não importado",
    importAction: "Importar",
    previewAction: "Visualizar",
    loadingManifest: "Carregando catálogo de baralhos...",
    loadingDeck: "Baixando e processando baralho...",
    successImport: "Baralho importado com sucesso!",
    cardCount: "Card {{current}} de {{total}}",
    flipPrompt: "Toque no card para ver o verso",
    noDecks: "Nenhum baralho disponível no momento para esta combinação.",
    close: "Fechar",
    errorLoading: "Erro ao carregar os dados. Verifique sua conexão.",
    storeTitle: "LOJA DE FLASHCARDS",
    prevCard: "Anterior",
    nextCard: "Próximo",
    deleteAction: "Excluir",
    confirmDelete: "Tem certeza que deseja excluir este baralho?",
    successDelete: "Baralho excluído com sucesso!"
  },
  en: {
    backToStore: "Back to Store",
    decksTitle: "Available Decks",
    size: "Size",
    downloaded: "Downloaded",
    notDownloaded: "Not downloaded",
    imported: "Imported",
    notImported: "Not imported",
    importAction: "Import",
    previewAction: "Preview",
    loadingManifest: "Loading deck catalog...",
    loadingDeck: "Downloading and processing deck...",
    successImport: "Deck imported successfully!",
    cardCount: "Card {{current}} of {{total}}",
    flipPrompt: "Tap card to flip",
    noDecks: "No decks currently available for this combination.",
    close: "Close",
    errorLoading: "Error loading data. Please check your connection.",
    storeTitle: "FLASHCARD STORE",
    prevCard: "Previous",
    nextCard: "Next",
    deleteAction: "Delete",
    confirmDelete: "Are you sure you want to delete this deck?",
    successDelete: "Deck deleted successfully!"
  },
  es: {
    backToStore: "Volver a la Tienda",
    decksTitle: "Mazos Disponibles",
    size: "Tamaño",
    downloaded: "Descargado",
    notDownloaded: "No descargado",
    imported: "Importado",
    notImported: "No importado",
    importAction: "Importar",
    previewAction: "Visualizar",
    loadingManifest: "Cargando catálogo...",
    loadingDeck: "Descargando mazo...",
    successImport: "¡Mazo importado con éxito!",
    cardCount: "Tarjeta {{current}} de {{total}}",
    flipPrompt: "Toca la tarjeta para voltear",
    noDecks: "No hay mazos disponibles para esta combinación.",
    close: "Cerrar",
    errorLoading: "Error al cargar datos. Revisa tu conexão.",
    storeTitle: "TIENDA DE FLASHCARDS",
    prevCard: "Anterior",
    nextCard: "Siguiente",
    deleteAction: "Eliminar",
    confirmDelete: "¿Estás seguro de que deseas eliminar este mazo?",
    successDelete: "¡Mazo eliminado con éxito!"
  },
  fr: {
    backToStore: "Retour à la Boutique",
    decksTitle: "Paquets Disponibles",
    size: "Taille",
    downloaded: "Téléchargé",
    notDownloaded: "Non téléchargé",
    imported: "Importé",
    notImported: "Non importé",
    importAction: "Importer",
    previewAction: "Aperçu",
    loadingManifest: "Chargement du catalogue...",
    loadingDeck: "Téléchargement du paquet...",
    successImport: "Paquet importé avec succès !",
    cardCount: "Carte {{current}} sur {{total}}",
    flipPrompt: "Appuyez pour retourner",
    noDecks: "Aucun paquet disponible pour cette combinaison.",
    close: "Fermer",
    errorLoading: "Erreur de chargement. Vérifiez votre connexion.",
    storeTitle: "BOUTIQUE DE FLASHCARDS",
    prevCard: "Précédent",
    nextCard: "Suivant",
    deleteAction: "Supprimer",
    confirmDelete: "Êtes-vous sûr de vouloir supprimer ce paquet?",
    successDelete: "Paquet supprimé avec succès !"
  },
  de: {
    backToStore: "Zurück zum Shop",
    decksTitle: "Verfügbare Stapel",
    size: "Größe",
    downloaded: "Heruntergeladen",
    notDownloaded: "Nicht heruntergeladen",
    imported: "Importiert",
    notImported: "Nicht importiert",
    importAction: "Importieren",
    previewAction: "Vorschau",
    loadingManifest: "Katalog wird geladen...",
    loadingDeck: "Stapel wird heruntergeladen...",
    successImport: "Stapel erfolgreich importiert!",
    cardCount: "Karte {{current}} von {{total}}",
    flipPrompt: "Tippen zum Wenden",
    noDecks: "Keine Stapel für diese Kombination verfügbar.",
    close: "Schließen",
    errorLoading: "Fehler beim Laden. Verbindung prüfen.",
    storeTitle: "KARTEIKARTEN-SHOP",
    prevCard: "Zurück",
    nextCard: "Weiter",
    deleteAction: "Löschen",
    confirmDelete: "Sind Sie sicher, dass Sie diesen Stapel löschen möchten?",
    successDelete: "Stapel erfolgreich gelöscht!"
  },
  it: {
    backToStore: "Torna al Negozio",
    decksTitle: "Mazzi Disponibili",
    size: "Dimensione",
    downloaded: "Scaricato",
    notDownloaded: "Non scaricato",
    imported: "Importato",
    notImported: "Non importato",
    importAction: "Importa",
    previewAction: "Anteprima",
    loadingManifest: "Caricamento catalogo...",
    loadingDeck: "Scaricamento mazzo...",
    successImport: "Mazzo importato con successo!",
    cardCount: "Carta {{current}} di {{total}}",
    flipPrompt: "Tocca per girare",
    noDecks: "Nessun mazzo disponibile per questa combinazione.",
    close: "Chiudi",
    errorLoading: "Errore durante il caricamento. Controlla la connessione.",
    storeTitle: "NEGOZIO DI FLASHCARD",
    prevCard: "Precedente",
    nextCard: "Successivo",
    deleteAction: "Elimina",
    confirmDelete: "Sei sicuro di voler eliminare questo mazzo?",
    successDelete: "Mazzo eliminato con successo!"
  },
  hu: {
    backToStore: "Vissza a Boltba",
    decksTitle: "Elérhető paklik",
    size: "Méret",
    downloaded: "Letöltve",
    notDownloaded: "Nincs letöltve",
    imported: "Importálva",
    notImported: "Nincs importálva",
    importAction: "Importálás",
    previewAction: "Előnézet",
    loadingManifest: "Katalógus betöltése...",
    loadingDeck: "Pakli letöltése...",
    successImport: "Pakli sikeresen importálva!",
    cardCount: "{{current}} / {{total}} kártya",
    flipPrompt: "Koppintson a megfordításhoz",
    noDecks: "Ehhez a kombinációhoz nincs elérhető pakli.",
    close: "Bezárás",
    errorLoading: "Betöltési hiba. Ellenőrizze a kapcsolatot.",
    storeTitle: "TANULÓKÁRTYA BOLT",
    prevCard: "Előző",
    nextCard: "Következő",
    deleteAction: "Törlés",
    confirmDelete: "Biztosan törölni szeretné ezt a paklit?",
    successDelete: "Pakli sikeresen törölve!"
  },
  ja: {
    backToStore: "ストアに戻る",
    decksTitle: "利用可能なデッキ",
    size: "サイズ",
    downloaded: "ダウンロード済み",
    notDownloaded: "未ダウンロード",
    imported: "インポート済み",
    notImported: "未インポート",
    importAction: "インポート",
    previewAction: "プレビュー",
    loadingManifest: "カタログを読み込み中...",
    loadingDeck: "デッキをダウンロード中...",
    successImport: "デッキがインポートされました！",
    cardCount: "{{total}}枚中{{current}}枚目",
    flipPrompt: "タップして裏返す",
    noDecks: "この組み合わせ of デッキはありません。",
    close: "閉じる",
    errorLoading: "読み込みエラー。接続を確認してください。",
    storeTitle: "フラッシュカードストア",
    prevCard: "前へ",
    nextCard: "次へ",
    deleteAction: "削除",
    confirmDelete: "このデッキを削除してもよろしいですか？",
    successDelete: "デッキが削除されました！"
  },
  zh: {
    backToStore: "返回商店",
    decksTitle: "可用卡片集",
    size: "大小",
    downloaded: "已下载",
    notDownloaded: "未下载",
    imported: "已导入",
    notImported: "未导入",
    importAction: "导入",
    previewAction: "预览",
    loadingManifest: "正在加载目录...",
    loadingDeck: "正在下载卡片集...",
    successImport: "卡片集导入成功！",
    cardCount: "第 {{current}} 张，共 {{total}} 张",
    flipPrompt: "点击翻面",
    noDecks: "该组合目前没有可用的卡片集。",
    close: "关闭",
    errorLoading: "加载错误，请检查网络连接。",
    storeTitle: "闪卡商店",
    prevCard: "上一张",
    nextCard: "下一张",
    deleteAction: "删除",
    confirmDelete: "您确定要删除这个卡片集吗？",
    successDelete: "卡片集删除成功！"
  },
  ru: {
    backToStore: "Назад в Магазин",
    decksTitle: "Доступные Колоды",
    size: "Размер",
    downloaded: "Скачано",
    notDownloaded: "Не скачано",
    imported: "Импортировано",
    notImported: "Не импортировано",
    importAction: "Импорт",
    previewAction: "Просмотр",
    loadingManifest: "Загрузка каталога...",
    loadingDeck: "Загрузка колоды...",
    successImport: "Колода успешно импортирована!",
    cardCount: "Карточка {{current}} из {{total}}",
    flipPrompt: "Нажмите, чтобы перевернуть",
    noDecks: "Нет доступных колод для этого направления.",
    close: "Закрыть",
    errorLoading: "Ошибка загрузки. Проверьте подключение.",
    storeTitle: "МАГАЗИН КАРТОЧЕК",
    prevCard: "Назад",
    nextCard: "Далее",
    deleteAction: "Удалить",
    confirmDelete: "Вы уверены, что хотите удалить эту колоду?",
    successDelete: "Колода успешно удалена!"
  }
};

interface DeckManifestItem {
  level: string;
  file: string;
  size_kb: number;
}

const FlashcardStoreSelectPage: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { nativeLang, targetLang } = useParams<{ nativeLang: string; targetLang: string }>();

  const currentLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
  const getTranslation = (key: string, replacements?: Record<string, string | number>) => {
    const translations = LOCAL_STORE_TRANSLATIONS[currentLang] || LOCAL_STORE_TRANSLATIONS.pt;
    let text = translations[key] || LOCAL_STORE_TRANSLATIONS.pt[key] || key;
    if (replacements) {
      Object.keys(replacements).forEach(k => {
        text = text.replace(`{{${k}}}`, String(replacements[k]));
      });
    }
    return text;
  };

  const getStoreTranslation = (key: string) => {
    const translations = STORE_TRANSLATIONS[currentLang] || STORE_TRANSLATIONS.pt;
    return translations[key] || STORE_TRANSLATIONS.pt[key];
  };

  const getLangName = (id: string) => {
    return getStoreTranslation(`lang_${id}`) || STORE_LANGUAGES.find(l => l.id === id)?.name || id;
  };

  // State Management
  const [activeStoreTab, setActiveStoreTab] = useState<'phrases' | 'words'>('phrases');
  const [manifest, setManifest] = useState<{ decks: DeckManifestItem[] } | null>(null);
  const [wordsData, setWordsData] = useState<any | null>(null);
  const [loadingWords, setLoadingWords] = useState(false);
  const [importedDecks, setImportedDecks] = useState<string[]>([]);
  const [downloadedLevels, setDownloadedLevels] = useState<string[]>([]);
  const [loadingManifest, setLoadingManifest] = useState(true);
  const [loadingActionDeck, setLoadingActionDeck] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  const R2_WORD_LANG_FOLDERS: Record<string, string> = {
    'en': 'Ingles', 'ingles': 'Ingles', 'english': 'Ingles',
    'pt': 'Portugues', 'ptbr': 'Portugues', 'portugues': 'Portugues', 'portuguese': 'Portugues',
    'fr': 'Frances', 'frances': 'Frances', 'french': 'Frances',
    'es': 'Espanhol', 'espanhol': 'Espanhol', 'spanish': 'Espanhol',
    'de': 'Alemao', 'alemao': 'Alemao', 'german': 'Alemao',
    'it': 'Italiano', 'italiano': 'Italiano', 'italian': 'Italiano',
    'ru': 'Russo', 'russo': 'Russo', 'russian': 'Russo',
    'hu': 'Hungaro', 'hungaro': 'Hungaro', 'hungarian': 'Hungaro'
  };

  const R2_WORD_SHORT_CODES: Record<string, string> = {
    'en': 'en', 'ingles': 'en', 'english': 'en',
    'pt': 'pt', 'ptbr': 'pt', 'portugues': 'pt', 'portuguese': 'pt',
    'fr': 'fr', 'frances': 'fr', 'french': 'fr',
    'es': 'es', 'espanhol': 'es', 'spanish': 'es',
    'de': 'de', 'alemao': 'de', 'german': 'de',
    'it': 'it', 'italiano': 'it', 'italian': 'it',
    'ru': 'ru', 'russo': 'ru', 'russian': 'ru',
    'hu': 'hu', 'hungaro': 'hu', 'hungarian': 'hu'
  };

  // Load downloaded levels from localFlashcardDb
  const checkDownloadedDecks = async () => {
    try {
      const keys = await localFlashcardDb.getAllStoreDeckKeys();
      const prefix = `fc_store_deck_${nativeLang}_${targetLang}_`;
      const levels = keys
        .filter(k => k.startsWith(prefix))
        .map(k => k.replace(prefix, ''));
      setDownloadedLevels(levels);
    } catch (e) {
      console.error('[Store] Error checking downloaded decks:', e);
    }
  };

  useEffect(() => {
    checkDownloadedDecks();
  }, [nativeLang, targetLang]);

  const fetchNewFlashcardsFileForLevel = async (native: string, target: string, level: string) => {
    const normNative = (native || 'pt').toLowerCase().replace(/[^a-z0-9]/g, '');
    const normTarget = (target || 'en').toLowerCase().replace(/[^a-z0-9]/g, '');

    const targetFolder = R2_WORD_LANG_FOLDERS[normTarget] || 'Ingles';
    const nativeFolder = R2_WORD_LANG_FOLDERS[normNative] || 'Portugues';

    const targetCode = R2_WORD_SHORT_CODES[normTarget] || 'en';
    const nativeCode = R2_WORD_SHORT_CODES[normNative] || 'pt';
    const lvl = level.toUpperCase();

    const candidates = [
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-flashcards/${targetFolder}/${lvl}/${targetCode}-${nativeCode}.json?t=${Date.now()}`,
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-flashcards/${targetFolder}/${lvl}/${targetCode}-${nativeCode}.json?t=${Date.now()}`,
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-flashcards/${nativeFolder}/${lvl}/${nativeCode}-${targetCode}.json?t=${Date.now()}`,
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-flashcards/${nativeFolder}/${lvl}/${nativeCode}-${targetCode}.json?t=${Date.now()}`,
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-flashcards/${targetFolder.toLowerCase()}/${nativeFolder.toLowerCase()}.json?t=${Date.now()}`,
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-flashcards/${targetFolder.toLowerCase()}/${nativeFolder.toLowerCase()}.json?t=${Date.now()}`
    ];

    for (const url of candidates) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json)) return json;
          if (json && json[lvl] && Array.isArray(json[lvl])) return json[lvl];
        }
      } catch (e) {}
    }
    return [];
  };

  const fetchNewFlashcardsFile = async (native: string, target: string) => {
    const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    const result: Record<string, any[]> = {};
    await Promise.all(levels.map(async (lvl) => {
      const list = await fetchNewFlashcardsFileForLevel(native, target, lvl);
      result[lvl] = list;
    }));
    return result;
  };

  // Load new-flashcards JSONs when switching to 'words' tab
  useEffect(() => {
    if (activeStoreTab === 'words' && !wordsData && !loadingWords) {
      const fetchWords = async () => {
        setLoadingWords(true);
        try {
          const json = await fetchNewFlashcardsFile(nativeLang || 'pt', targetLang || 'en');
          if (json) {
            setWordsData(json);
          }
        } catch (err) {
          console.error('Error loading new flashcards JSON:', err);
        } finally {
          setLoadingWords(false);
        }
      };
      fetchWords();
    }
  }, [activeStoreTab, targetLang, nativeLang, wordsData, loadingWords]);

  // Preview States
  const [previewDeck, setPreviewDeck] = useState<any[] | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [previewLevel, setPreviewLevel] = useState<string | null>(null);

  const nativeObj = STORE_LANGUAGES.find(l => l.id === nativeLang);
  const targetObj = STORE_LANGUAGES.find(l => l.id === targetLang);

  // Load manifest.json and user's imported decks
  useEffect(() => {
    let hasLoadedCache = false;
    try {
      const cachedImportedKey = user ? `libritui_cache_store_imported_${user.id}_${nativeLang}_${targetLang}` : null;
      const cachedImported = cachedImportedKey ? localStorage.getItem(cachedImportedKey) : null;
      if (cachedImported) {
        setImportedDecks(JSON.parse(cachedImported));
      }
    } catch (e) {
      console.error('Error loading store cache:', e);
    }

    const loadCatalog = async () => {
      try {
        if (!hasLoadedCache) {
          setLoadingManifest(true);
        }
        // Step 1: Fetch manifest
        const manifestRes = await fetch(`https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/R2-consolidated-flashcards/words-counted/manifest.json?t=${Date.now()}`);
        if (!manifestRes.ok) throw new Error('Manifest load failed');
        const manifestData = await manifestRes.json();
        setManifest(manifestData);
        try {
          localStorage.setItem('libritui_cache_store_manifest', JSON.stringify(manifestData));
        } catch (e) {
          console.error(e);
        }

        // Step 2: Fetch imported decks for user
        if (user) {
          const importedRes = await dbManager.from('flashcards_imported', user.id).select({ user_id: user.id });
          const list = Array.isArray(importedRes) ? importedRes : (importedRes?.data || []);
          const activeDecks = list
            .filter((d: any) => d.idioma_nativo === nativeLang && d.idioma_alvo === targetLang)
            .map((d: any) => d.deck_id);
          setImportedDecks(activeDecks);
          try {
            localStorage.setItem(`libritui_cache_store_imported_${user.id}_${nativeLang}_${targetLang}`, JSON.stringify(activeDecks));
          } catch (e) {
            console.error(e);
          }
        }
        setErrorMsg(null);
      } catch (err: any) {
        console.error('Error loading store data:', err);
        if (!hasLoadedCache) {
          setErrorMsg(getTranslation('errorLoading'));
        }
      } finally {
        setLoadingManifest(false);
      }
    };

    loadCatalog();
  }, [user, nativeLang, targetLang]);

  // Toast auto-dismiss
  useEffect(() => {
    if (successToast) {
      const t = setTimeout(() => setSuccessToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [successToast]);

  // Helper: Download a deck and return the JSON payload
  const downloadDeckJSON = async (deck: DeckManifestItem): Promise<any[]> => {
    const levelUpper = deck.level.toUpperCase();
    if (levelUpper === 'A1') {
      const R2_LANG_NAMES: Record<string, string> = {
        'de': 'Alemão',
        'es': 'Espanhol',
        'fr': 'Francês',
        'hu': 'Húngaro',
        'en': 'Inglês',
        'it': 'Italiano',
        'ptbr': 'Português',
        'ru': 'Russo'
      };
      
      const targetLangName = R2_LANG_NAMES[targetLang || ''] || targetLang;
      const nativeLangName = R2_LANG_NAMES[nativeLang || ''] || nativeLang;
      
      const targetUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/A1/${encodeURIComponent(targetLangName)} - A1.json?t=${Date.now()}`;
      const nativeUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/A1/${encodeURIComponent(nativeLangName)} - A1.json?t=${Date.now()}`;

      const [targetRes, nativeRes] = await Promise.all([
        fetch(targetUrl),
        fetch(nativeUrl)
      ]);

      if (!targetRes.ok) throw new Error(`Failed to download target language A1 deck`);
      if (!nativeRes.ok) throw new Error(`Failed to download native language A1 deck`);

      const targetData = await targetRes.json();
      const nativeData = await nativeRes.json();

      if (!Array.isArray(targetData) || !Array.isArray(nativeData)) {
        throw new Error('Invalid JSON format for split A1 decks');
      }

      return targetData.map((item: any, idx: number) => {
        const nativeItem = nativeData[idx] || {};
        return {
          id_base: String(idx + 1),
          [targetLang || 'en']: item.text || '',
          [nativeLang || 'ptbr']: nativeItem.text || ''
        };
      });
    }

    const filename = deck.file.endsWith('_Global.json') ? deck.file : deck.file.replace('.json', '_Global.json');
    const url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/R2-consolidated-flashcards/words-counted-new/${filename}?t=${Date.now()}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to download ${filename}`);
    const data = await res.json();
    if (!Array.isArray(data)) throw new Error('Invalid JSON format');
    return data;
  };

  // Helper: Save deck JSON to IndexedDB
  const saveDeckLocally = async (level: string, data: any[]) => {
    const key = `fc_store_deck_${nativeLang}_${targetLang}_${level}`;
    await localFlashcardDb.saveStoreDeck(key, data);
    await checkDownloadedDecks();
  };

  // Helper: Read deck JSON from IndexedDB
  const readDeckLocally = async (level: string): Promise<any[] | null> => {
    const key = `fc_store_deck_${nativeLang}_${targetLang}_${level}`;
    const data = await localFlashcardDb.getStoreDeck(key);
    if (!data) return null;
    try {
      if (Array.isArray(data) && data.length > 0) {
        const hasWords = Object.keys(data[0]).some(k => k.endsWith('-words') || k.endsWith('_words'));
        if (!hasWords) {
          await localFlashcardDb.deleteStoreDeck(key);
          await checkDownloadedDecks();
          return null;
        }
      }
      return data;
    } catch {
      return null;
    }
  };

  // Action: Import / Save Deck
  const handleImport = async (deck: DeckManifestItem) => {
    if (!user) return;
    try {
      setLoadingActionDeck(deck.level);
      
      // Download if not already saved locally (ignore local cache for A1 to force updates)
      let deckData = deck.level.toUpperCase() === 'A1' ? null : await readDeckLocally(deck.level);
      if (!deckData) {
        deckData = await downloadDeckJSON(deck);
        await saveDeckLocally(deck.level, deckData);
      }

      // Save record in D1 Database
      await dbManager.from('flashcards_imported', user.id).insert({
        user_id: user.id,
        deck_id: deck.level,
        idioma_nativo: nativeLang,
        idioma_alvo: targetLang,
        caminho_local_do_arquivo: `fc_store_deck_${nativeLang}_${targetLang}_${deck.level}`
      });

      const updatedDecks = [...importedDecks, deck.level];
      setImportedDecks(updatedDecks);
      try {
        localStorage.setItem(`libritui_cache_store_imported_${user.id}_${nativeLang}_${targetLang}`, JSON.stringify(updatedDecks));
        localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);
        localStorage.removeItem(`libritui_cache_d1_flashcards_${user.id}`);
      } catch (e) {
        console.error(e);
      }
      window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
      setSuccessToast(getTranslation('successImport'));
    } catch (err: any) {
      console.error('Failed to import deck:', err);
      alert(err.message || 'Error importing deck');
    } finally {
      setLoadingActionDeck(null);
    }
  };

  // Action: Delete / Remove Deck
  const handleDelete = async (level: string) => {
    if (!user) return;
    if (!window.confirm(getTranslation('confirmDelete'))) return;
    
    try {
      setLoadingActionDeck(level);
      
      // Delete record in D1 Database
      await dbManager.from('flashcards_imported', user.id).deleteMatch({
        deck_id: level,
        idioma_nativo: nativeLang,
        idioma_alvo: targetLang
      });

      // Delete locally from IndexedDB
      const key = `fc_store_deck_${nativeLang}_${targetLang}_${level}`;
      await localFlashcardDb.deleteStoreDeck(key);
      await checkDownloadedDecks();

      const updatedDecks = importedDecks.filter(l => l !== level);
      setImportedDecks(updatedDecks);
      try {
        localStorage.setItem(`libritui_cache_store_imported_${user.id}_${nativeLang}_${targetLang}`, JSON.stringify(updatedDecks));
        localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);
        localStorage.removeItem(`libritui_cache_d1_flashcards_${user.id}`);
      } catch (e) {
        console.error(e);
      }
      // Dispatch refresh event to sync with other parts
      window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
      setSuccessToast(getTranslation('successDelete'));
    } catch (err: any) {
      console.error('Failed to delete deck:', err);
      alert(err.message || 'Error deleting deck');
    } finally {
      setLoadingActionDeck(null);
    }
  };

  // Action: Open Preview View
  const handlePreview = async (deck: DeckManifestItem) => {
    try {
      setLoadingActionDeck(deck.level);
      
      let deckData = deck.level.toUpperCase() === 'A1' ? null : await readDeckLocally(deck.level);
      if (!deckData) {
        deckData = await downloadDeckJSON(deck);
        await saveDeckLocally(deck.level, deckData);
      }

      setPreviewDeck(deckData);
      setPreviewIndex(0);
      setPreviewFlipped(false);
      setPreviewLevel(deck.level);
    } catch (err: any) {
      console.error('Failed to load preview:', err);
      alert(err.message || 'Error loading preview');
    } finally {
      setLoadingActionDeck(null);
    }
  };

  const handleImportWordDeck = async (level: string) => {
    if (!user) return;
    const deckId = `words_${level}`;
    try {
      setLoadingActionDeck(deckId);

      let dataJson = wordsData;
      if (!dataJson || !dataJson[level]) {
        dataJson = await fetchNewFlashcardsFile(nativeLang || 'pt', targetLang || 'en');
        if (dataJson) setWordsData(dataJson);
      }

      const rawList = dataJson ? (dataJson[level] || []) : [];

      const merged = rawList.map((item: any, idx: number) => {
        const primaryTrans = (Array.isArray(item.translations) && item.translations.length > 0)
          ? item.translations[0]
          : (item.translation || item.word || '');

        const secondaryTrans = (Array.isArray(item.translations) && item.translations.length > 1)
          ? item.translations.slice(1)
          : [];

        return {
          id_base: `w_${level}_${item.id || idx + 1}`,
          word: item.word || '',
          translations: item.translations || [primaryTrans, ...secondaryTrans],
          primary_translation: primaryTrans,
          secondary_translations: secondaryTrans,
          [targetLang || 'en']: item.word || '',
          [nativeLang || 'ptbr']: primaryTrans
        };
      });

      const localKey = `fc_store_deck_${nativeLang}_${targetLang}_${deckId}`;
      await localFlashcardDb.saveStoreDeck(localKey, merged);

      await dbManager.from('flashcards_imported', user.id).insert({
        user_id: user.id,
        deck_id: deckId,
        idioma_nativo: nativeLang,
        idioma_alvo: targetLang,
        caminho_local_do_arquivo: localKey
      });

      const updatedDecks = [...importedDecks, deckId];
      setImportedDecks(updatedDecks);
      try {
        localStorage.setItem(`libritui_cache_store_imported_${user.id}_${nativeLang}_${targetLang}`, JSON.stringify(updatedDecks));
        localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);
        localStorage.removeItem(`libritui_cache_d1_flashcards_${user.id}`);
      } catch (e) {
        console.error(e);
      }
      window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
      setSuccessToast(getTranslation('successImport'));
    } catch (err: any) {
      console.error('Failed to import word deck:', err);
      alert(err.message || 'Error importing deck');
    } finally {
      setLoadingActionDeck(null);
    }
  };

  const handleDeleteWordDeck = async (level: string) => {
    if (!user) return;
    if (!window.confirm(getTranslation('confirmDelete'))) return;

    const deckId = `words_${level}`;
    try {
      setLoadingActionDeck(deckId);

      await dbManager.from('flashcards_imported', user.id).deleteMatch({
        deck_id: deckId,
        idioma_nativo: nativeLang,
        idioma_alvo: targetLang
      });

      const localKey = `fc_store_deck_${nativeLang}_${targetLang}_${deckId}`;
      await localFlashcardDb.deleteStoreDeck(localKey);

      const updatedDecks = importedDecks.filter(l => l !== deckId);
      setImportedDecks(updatedDecks);
      try {
        localStorage.setItem(`libritui_cache_store_imported_${user.id}_${nativeLang}_${targetLang}`, JSON.stringify(updatedDecks));
        localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);
        localStorage.removeItem(`libritui_cache_d1_flashcards_${user.id}`);
      } catch (e) {
        console.error(e);
      }
      window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
      setSuccessToast(getTranslation('successDelete'));
    } catch (err: any) {
      console.error('Failed to delete word deck:', err);
      alert(err.message || 'Error deleting deck');
    } finally {
      setLoadingActionDeck(null);
    }
  };

  const handlePreviewWordDeck = async (level: string) => {
    const deckId = `words_${level}`;
    try {
      setLoadingActionDeck(deckId);
      let dataJson = wordsData;
      if (!dataJson || !dataJson[level]) {
        dataJson = await fetchNewFlashcardsFile(nativeLang || 'pt', targetLang || 'en');
        if (dataJson) setWordsData(dataJson);
      }

      const rawList = dataJson ? (dataJson[level] || []) : [];

      const merged = rawList.map((item: any, idx: number) => {
        const primaryTrans = (Array.isArray(item.translations) && item.translations.length > 0)
          ? item.translations[0]
          : (item.translation || item.word || '');

        const secondaryTrans = (Array.isArray(item.translations) && item.translations.length > 1)
          ? item.translations.slice(1)
          : [];

        return {
          id_base: `w_${level}_${item.id || idx + 1}`,
          word: item.word || '',
          translations: item.translations || [primaryTrans, ...secondaryTrans],
          primary_translation: primaryTrans,
          secondary_translations: secondaryTrans,
          [targetLang || 'en']: item.word || '',
          [nativeLang || 'ptbr']: primaryTrans
        };
      });

      setPreviewDeck(merged);
      setPreviewIndex(0);
      setPreviewFlipped(false);
      setPreviewLevel(`Palavras ${level}`);
    } catch (err: any) {
      console.error('Failed to load preview:', err);
      alert(err.message || 'Error loading preview');
    } finally {
      setLoadingActionDeck(null);
    }
  };

  return (
    <div className="min-h-full flex flex-col bg-tint-stone/40 pb-24 relative overflow-x-hidden">
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>

      {/* Toast Notification */}
      {successToast && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[100] bg-ok text-surface font-bold px-6 py-4 rounded-2xl flex items-center gap-3 animate-bounce">
          <Check size={18} />
          <span className="text-xs tracking-[0.14em] uppercase font-mono">{successToast}</span>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 p-6 md:p-8 flex items-center justify-between z-40 bg-tint-stone/90 backdrop-blur-md">
        <AppBackButton fallbackTo="/flashcards/store" />

        {nativeObj && targetObj && (
          <div className="flex items-center gap-3 px-5 py-2.5 bg-surface dark:bg-surface rounded-full border border-black/[0.06] dark:border-white/[0.06]">
            <span className="text-lg">{nativeObj.flag}</span>
            <span className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">{getLangName(nativeObj.id)}</span>
            <span className="text-muted/60 text-sm">→</span>
            <span className="text-lg">{targetObj.flag}</span>
            <span className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">{getLangName(targetObj.id)}</span>
          </div>
        )}
      </div>

      {/* Main Body */}
      <div className="flex-1 flex flex-col p-6 max-w-4xl mx-auto w-full space-y-8 mt-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl font-medium text-ink tracking-[-0.03em]">
              {getTranslation('decksTitle')}
            </h1>
            <p className="text-[10px] font-normal tracking-[0.14em] text-muted uppercase font-mono">
              {getTranslation('storeTitle')}
            </p>
          </div>

          {/* Top Tabs: Frases vs Palavras */}
          <div className="flex items-center gap-2 bg-tint-stone/60 p-1.5 rounded-2xl">
            <button
              onClick={() => setActiveStoreTab('phrases')}
              className={`px-6 py-2.5 rounded-xl font-normal text-xs uppercase font-mono tracking-[0.14em] transition-all ${
                activeStoreTab === 'phrases'
                  ? 'bg-surface dark:bg-surface text-primary'
                  : 'text-muted hover:text-ink '
              }`}
            >
              Frases
            </button>
            <button
              onClick={() => setActiveStoreTab('words')}
              className={`px-6 py-2.5 rounded-xl font-normal text-xs uppercase font-mono tracking-[0.14em] transition-all ${
                activeStoreTab === 'words'
                  ? 'bg-surface dark:bg-surface text-primary'
                  : 'text-muted hover:text-ink '
              }`}
            >
              Palavras
            </button>
          </div>
        </div>

        {/* Phrases Tab */}
        {activeStoreTab === 'phrases' && (
          loadingManifest ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 space-y-4">
              <Loader2 className="text-primary animate-spin" size={40} />
              <p className="text-xs font-normal text-muted uppercase font-mono tracking-[0.14em]">
                {getTranslation('loadingManifest')}
              </p>
            </div>
          ) : errorMsg ? (
            <div className="text-center py-20 text-bad font-normal uppercase font-mono text-xs tracking-[0.14em]">
              {errorMsg}
            </div>
          ) : !manifest || manifest.decks.length === 0 ? (
            <div className="text-center py-20 text-muted font-normal uppercase font-mono text-xs tracking-[0.14em]">
              {getTranslation('noDecks')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {manifest.decks.map(deck => {
                const isImported = importedDecks.includes(deck.level);
                const isActionLoading = loadingActionDeck === deck.level;
                const cardCountText = deck.level.toUpperCase() === 'A1' ? '1.500 FLASHCARDS' : '1.000 FLASHCARDS';

                return (
                  <div 
                    key={deck.level}
                    className="bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] p-6 hover: transition-all duration-300 relative flex flex-col justify-between"
                  >
                    <div className="space-y-4">
                      {/* Badge / Level Title & Card Count */}
                      <div className="flex justify-between items-start">
                        <div className="space-y-1.5">
                          <span className="px-4 py-1.5 bg-primary/10 dark:bg-primary/20 text-primary font-medium text-[13px] rounded-full">
                            Nível {deck.level}
                          </span>
                          <div className="text-[11px] font-normal text-ink uppercase font-mono tracking-[0.14em] pt-1">
                            {cardCountText}
                          </div>
                          {!isImported && (
                            <div className="text-[10px] font-normal text-muted uppercase tracking-[0.14em] font-mono">
                              {getTranslation('size')}: {deck.size_kb} KB
                            </div>
                          )}
                        </div>
                        
                        {/* State Tags: Only Importado / Não Importado */}
                        <div className="flex flex-col gap-1.5 items-end">
                          {isImported ? (
                            <span className="flex items-center gap-1 text-[9px] font-medium text-ok bg-ok/10 dark:bg-ok/20 px-2.5 py-1 rounded-full">
                              <Check size={10} />
                              {getTranslation('imported')}
                            </span>
                          ) : (
                            <span className="text-[9px] font-medium text-muted bg-tint-stone/60 px-2.5 py-1 rounded-full">
                              {getTranslation('notImported')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-8">
                      <button
                        onClick={() => handlePreview(deck)}
                        disabled={isActionLoading}
                        className="text-[13px] font-medium flex-1 h-12 bg-tint-stone/40 hover:bg-tint-stone/60 border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                      >
                        {isActionLoading && loadingActionDeck === deck.level ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <Eye size={14} />
                        )}
                        {getTranslation('previewAction')}
                      </button>

                      {isImported ? (
                        <button
                          onClick={() => handleDelete(deck.level)}
                          disabled={isActionLoading}
                          className="text-[13px] font-medium flex-1 h-12 bg-bad/10 hover:bg-bad/20 text-bad border border-bad/20 rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                        >
                          {isActionLoading && loadingActionDeck === deck.level ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <Trash2 size={14} />
                          )}
                          {getTranslation('deleteAction')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleImport(deck)}
                          disabled={isActionLoading}
                          className="text-[13px] font-medium flex-1 h-12 bg-primary hover:opacity-95 text-slate-900 rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                        >
                          {isActionLoading && loadingActionDeck === deck.level ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <Download size={14} />
                          )}
                          {getTranslation('importAction')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Words Tab */}
        {activeStoreTab === 'words' && (
          loadingWords ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 space-y-4">
              <Loader2 className="text-primary animate-spin" size={40} />
              <p className="text-xs font-normal text-muted uppercase font-mono tracking-[0.14em]">
                Carregando baralhos de palavras...
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(lvl => {
                const deckId = `words_${lvl}`;
                const isImported = importedDecks.includes(deckId);
                const isActionLoading = loadingActionDeck === deckId;
                const count = wordsData?.[lvl]?.length || wordsData?.target?.[lvl]?.length || 1000;
                const cardCountText = `${count.toLocaleString('pt-BR')} FLASHCARDS`;
                const approxSizeKb = Math.round(count * 0.08);

                return (
                  <div 
                    key={lvl}
                    className="bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] p-6 hover: transition-all duration-300 relative flex flex-col justify-between"
                  >
                    <div className="space-y-4">
                      {/* Badge / Level Title & Card Count */}
                      <div className="flex justify-between items-start">
                        <div className="space-y-1.5">
                          <span className="px-4 py-1.5 bg-primary/10 dark:bg-primary/20 text-primary font-medium text-[13px] rounded-full">
                            Nível {lvl}
                          </span>
                          <div className="text-[11px] font-normal text-ink uppercase font-mono tracking-[0.14em] pt-1">
                            {cardCountText}
                          </div>
                          {!isImported && (
                            <div className="text-[10px] font-normal text-muted uppercase tracking-[0.14em] font-mono">
                              {getTranslation('size')}: {approxSizeKb} KB
                            </div>
                          )}
                        </div>
                        
                        {/* State Tags: Only Importado / Não Importado */}
                        <div className="flex flex-col gap-1.5 items-end">
                          {isImported ? (
                            <span className="flex items-center gap-1 text-[9px] font-medium text-ok bg-ok/10 dark:bg-ok/20 px-2.5 py-1 rounded-full">
                              <Check size={10} />
                              {getTranslation('imported')}
                            </span>
                          ) : (
                            <span className="text-[9px] font-medium text-muted bg-tint-stone/60 px-2.5 py-1 rounded-full">
                              {getTranslation('notImported')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-8">
                      <button
                        onClick={() => handlePreviewWordDeck(lvl)}
                        disabled={isActionLoading}
                        className="text-[13px] font-medium flex-1 h-12 bg-tint-stone/40 hover:bg-tint-stone/60 border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                      >
                        {isActionLoading ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <Eye size={14} />
                        )}
                        {getTranslation('previewAction')}
                      </button>

                      {isImported ? (
                        <button
                          onClick={() => handleDeleteWordDeck(lvl)}
                          disabled={isActionLoading}
                          className="text-[13px] font-medium flex-1 h-12 bg-bad/10 hover:bg-bad/20 text-bad border border-bad/20 rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                        >
                          {isActionLoading ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <Trash2 size={14} />
                          )}
                          {getTranslation('deleteAction')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleImportWordDeck(lvl)}
                          disabled={isActionLoading}
                          className="text-[13px] font-medium flex-1 h-12 bg-primary hover:opacity-95 text-slate-900 rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                        >
                          {isActionLoading ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <Download size={14} />
                          )}
                          {getTranslation('importAction')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Card Preview Modal */}
      {previewDeck && previewLevel && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-md flex flex-col justify-between p-6">
          {/* Preview Header */}
          <div className="flex items-center justify-between w-full max-w-2xl mx-auto py-2">
            <div className="space-y-0.5">
              <h3 className="text-white font-medium text-sm">
                Preview: Nível {previewLevel}
              </h3>
              <p className="text-[10px] text-muted font-normal uppercase font-mono tracking-[0.14em]">
                {getTranslation('cardCount', { current: previewIndex + 1, total: previewDeck.length })}
              </p>
            </div>
            <button
              onClick={() => setPreviewDeck(null)}
              className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl transition-all"
            >
              <X size={18} />
            </button>
          </div>

          {/* Interactive Card */}
          <div className="flex-1 flex items-center justify-center w-full max-w-2xl mx-auto">
            <div 
              onClick={() => setPreviewFlipped(!previewFlipped)}
              className="w-full aspect-[4/3] max-h-[360px] perspective-1000 cursor-pointer group"
            >
              <div className={`w-full h-full duration-500 transform-style-3d relative ${previewFlipped ? 'rotate-y-180' : ''}`}>
                
                {/* Front Side */}
                <div className="absolute inset-0 bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 flex flex-col justify-between backface-hidden shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)]">
                  <div className="flex justify-between items-start">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-[9px] font-medium rounded-full">
                      {targetLang?.toUpperCase()}
                    </span>
                    <Sparkles size={16} className="text-primary/40 animate-pulse" />
                  </div>
                  <div className="text-center py-6">
                    <p className="text-lg md:text-2xl font-medium text-ink leading-relaxed select-none">
                      {previewDeck[previewIndex]?.[targetLang || 'en'] || '---'}
                    </p>
                  </div>
                  <div className="text-center text-[9px] font-normal text-muted uppercase font-mono tracking-[0.14em] animate-pulse">
                    {getTranslation('flipPrompt')}
                  </div>
                </div>

                {/* Back Side */}
                <div className="absolute inset-0 bg-ink text-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 flex flex-col justify-between backface-hidden rotate-y-180 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)]">
                  <div className="flex justify-between items-start">
                    <span className="px-3 py-1 bg-white/10 text-white/80 text-[9px] font-medium rounded-full">
                      {nativeLang?.toUpperCase()}
                    </span>
                    <BookOpen size={16} className="text-white/20" />
                  </div>
                  <div className="text-center py-6 overflow-y-auto custom-scrollbar flex-1 flex flex-col justify-center items-center">
                    {(() => {
                      const cardItem = previewDeck[previewIndex];
                      const translationsList = Array.isArray(cardItem?.translations)
                        ? cardItem.translations
                        : (cardItem?.secondary_translations ? [cardItem.primary_translation || cardItem[nativeLang || 'ptbr'] || cardItem.translation || cardItem.word, ...cardItem.secondary_translations] : null);

                      if (translationsList && translationsList.length > 0) {
                        const primary = translationsList[0];
                        const secondaries = translationsList.slice(1);
                        return (
                          <div className="flex flex-col items-center justify-center space-y-2 w-full">
                            {/* Primary Translation - Google Tradutor (Em destaque, maior e em negrito) */}
                            <p className="text-xl md:text-3xl font-medium text-white leading-relaxed select-none">
                              {primary}
                            </p>
                            {/* Secondary Translations - FreeDict (Menor, sem negrito, discreto) */}
                            {secondaries.length > 0 && (
                              <p className="text-xs md:text-sm font-normal text-muted max-w-md select-none tracking-wide">
                                {secondaries.join(', ')}
                              </p>
                            )}
                          </div>
                        );
                      }

                      return (
                        <p className="text-lg md:text-2xl font-medium text-white leading-relaxed select-none">
                          {cardItem?.[nativeLang || 'ptbr'] || cardItem?.translation || cardItem?.word || '---'}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="text-center text-[9px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
                    {getTranslation('flipPrompt')}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Preview Navigation Controls */}
          <div className="w-full max-w-2xl mx-auto flex items-center justify-between pb-8">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewIndex(prev => Math.max(0, prev - 1));
                setPreviewFlipped(false);
              }}
              disabled={previewIndex === 0}
              className="px-6 py-4 bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white font-medium text-[13px] rounded-2xl transition-all flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              {getTranslation('prevCard')}
            </button>

            {/* Quick Import inside preview */}
            {!importedDecks.includes(previewLevel) ? (
              <button
                onClick={() => {
                  const item = manifest?.decks.find(d => d.level === previewLevel);
                  if (item) handleImport(item);
                }}
                className="px-8 py-4 bg-primary hover:opacity-95 text-slate-900 font-medium text-[13px] rounded-2xl transition-all flex items-center gap-2"
              >
                <Download size={14} />
                {getTranslation('importAction')}
              </button>
            ) : (
              <button
                disabled
                className="px-8 py-4 bg-ok/10 text-ok border border-ok/20 font-medium text-[13px] rounded-2xl flex items-center gap-2 transition-all opacity-80"
              >
                <Check size={14} />
                {getTranslation('imported')}
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewIndex(prev => Math.min(previewDeck.length - 1, prev + 1));
                setPreviewFlipped(false);
              }}
              disabled={previewIndex === previewDeck.length - 1}
              className="px-6 py-4 bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white font-medium text-[13px] rounded-2xl transition-all flex items-center gap-2"
            >
              {getTranslation('nextCard')}
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlashcardStoreSelectPage;
